import React, { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useNavigationStore from '../../stores/navigationStore';
import { useDialogStore } from '../../stores/dialogStore';
import { useAuthStore } from '../../stores/authStore';

// Sortable table row for menu item
function SortableMenuItem({ item, systemPages, visibilityOptions, onUpdate, onDelete }) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(item.title);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleTitleSave = () => {
    if (editedTitle.trim() && editedTitle !== item.title) {
      onUpdate(item.id, { title: editedTitle.trim() });
    }
    setIsEditingTitle(false);
  };

  const handleFieldUpdate = (field, value) => {
    onUpdate(item.id, { [field]: value });
  };

  return (
    <tr ref={setNodeRef} style={style} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
      {/* Drag handle */}
      <td className="px-2 py-2 text-center">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          title="Drag to reorder"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </button>
      </td>

      {/* Title */}
      <td className="px-3 py-2 min-w-[120px]">
        {isEditingTitle ? (
          <input
            type="text"
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            onBlur={handleTitleSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleTitleSave();
              if (e.key === 'Escape') {
                setEditedTitle(item.title);
                setIsEditingTitle(false);
              }
            }}
            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            autoFocus
          />
        ) : (
          <div
            onClick={() => setIsEditingTitle(true)}
            className="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
          >
            {item.title}
          </div>
        )}
      </td>

      {/* Page */}
      <td className="px-3 py-2 min-w-[150px]">
        <select
          value={item.page_id || ''}
          onChange={(e) => handleFieldUpdate('page_id', e.target.value || null)}
          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
        >
          <option value="">-- None --</option>
          {systemPages.map((page) => (
            <option key={page.id} value={page.id}>
              {page.slug}
            </option>
          ))}
        </select>
      </td>

      {/* Visibility */}
      <td className="px-3 py-2 min-w-[120px]">
        <select
          value={item.visibility_id || ''}
          onChange={(e) => handleFieldUpdate('visibility_id', e.target.value || null)}
          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
        >
          <option value="">Public</option>
          {visibilityOptions.map((vis) => (
            <option key={vis.id} value={vis.id}>
              {vis.label}
            </option>
          ))}
        </select>
      </td>

      {/* Access Level */}
      <td className="px-3 py-2 min-w-[80px]">
        <input
          type="number"
          value={item.access_level || 0}
          onChange={(e) => handleFieldUpdate('access_level', parseInt(e.target.value) || 0)}
          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm text-center"
          min="0"
          max="255"
        />
      </td>

      {/* Active */}
      <td className="px-3 py-2 text-center min-w-[60px]">
        <input
          type="checkbox"
          checked={item.is_active === 1}
          onChange={(e) => handleFieldUpdate('is_active', e.target.checked ? 1 : 0)}
          className="rounded border-gray-300 dark:border-gray-600"
        />
      </td>

      {/* Master */}
      <td className="px-3 py-2 text-center min-w-[60px]">
        <input
          type="checkbox"
          checked={item.show_on_master_profile === 1}
          onChange={(e) => handleFieldUpdate('show_on_master_profile', e.target.checked ? 1 : 0)}
          className="rounded border-gray-300 dark:border-gray-600"
        />
      </td>

      {/* Slave */}
      <td className="px-3 py-2 text-center min-w-[60px]">
        <input
          type="checkbox"
          checked={item.show_on_slave_profile === 1}
          onChange={(e) => handleFieldUpdate('show_on_slave_profile', e.target.checked ? 1 : 0)}
          className="rounded border-gray-300 dark:border-gray-600"
        />
      </td>

      {/* Switch */}
      <td className="px-3 py-2 text-center min-w-[60px]">
        <input
          type="checkbox"
          checked={item.show_on_switch_profile === 1}
          onChange={(e) => handleFieldUpdate('show_on_switch_profile', e.target.checked ? 1 : 0)}
          className="rounded border-gray-300 dark:border-gray-600"
        />
      </td>

      {/* Observer */}
      <td className="px-3 py-2 text-center min-w-[70px]">
        <input
          type="checkbox"
          checked={item.show_on_observer_profile === 1}
          onChange={(e) => handleFieldUpdate('show_on_observer_profile', e.target.checked ? 1 : 0)}
          className="rounded border-gray-300 dark:border-gray-600"
        />
      </td>

      {/* Delete */}
      <td className="px-2 py-2 text-center">
        <button
          onClick={() => onDelete(item.id)}
          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          title="Delete"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </td>
    </tr>
  );
}

// Menu section component with table layout
function MenuSection({ menu, systemPages, visibilityOptions, onUpdate, onDelete, onReorder, onAdd }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [items, setItems] = useState(menu.items);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setItems(menu.items);
  }, [menu.items]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);

        // Send reorder request
        const orderedIds = newItems.map((item) => item.id);
        onReorder(menu.menu_type_code, orderedIds);

        return newItems;
      });
    }
  };

  return (
    <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <svg
            className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          {menu.menu_type_label}
          <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
            ({items.length})
          </span>
        </button>

        <button
          onClick={() => onAdd(menu.menu_id)}
          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium"
        >
          + Add
        </button>
      </div>

      {isExpanded && (
        <div className="overflow-x-auto">
          {items.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No menu items. Click "+ Add" to create one.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-100 dark:bg-gray-900 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">
                <tr>
                  <th className="px-2 py-2 w-10"></th>
                  <th className="px-3 py-2 text-left min-w-[120px]">Title</th>
                  <th className="px-3 py-2 text-left min-w-[150px]">Page</th>
                  <th className="px-3 py-2 text-left min-w-[120px]">Visibility</th>
                  <th className="px-3 py-2 text-center min-w-[80px]">Access</th>
                  <th className="px-3 py-2 text-center min-w-[60px]">Active</th>
                  <th className="px-3 py-2 text-center min-w-[60px]">Master</th>
                  <th className="px-3 py-2 text-center min-w-[60px]">Slave</th>
                  <th className="px-3 py-2 text-center min-w-[60px]">Switch</th>
                  <th className="px-3 py-2 text-center min-w-[70px]">Observer</th>
                  <th className="px-2 py-2 w-10"></th>
                </tr>
              </thead>              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                  <tbody className="text-sm text-gray-900 dark:text-gray-100">
                    {items.map((item) => (
                      <SortableMenuItem
                        key={item.id}
                        item={item}
                        systemPages={systemPages}
                        visibilityOptions={visibilityOptions}
                        onUpdate={onUpdate}
                        onDelete={onDelete}
                      />
                    ))}
                  </tbody>
                </SortableContext>
              </DndContext>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// Add Menu Item Modal
function AddMenuItemModal({ isOpen, onClose, onSubmit }) {
  const [title, setTitle] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (title.trim()) {
      onSubmit(title.trim());
      setTitle('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Add Menu Item</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Enter menu item title"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!title.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Main NavigationWidget component
export default function NavigationWidget() {
  const { accessToken } = useAuthStore();
  const {
    menus,
    systemPages,
    visibilityOptions,
    loading,
    error,
    fetchMenuItems,
    fetchSystemPages,
    fetchVisibilityOptions,
    updateMenuItem,
    deleteMenuItem,
    reorderMenuItems,
    createMenuItem,
  } = useNavigationStore();

  const { confirm } = useDialogStore();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [currentMenuId, setCurrentMenuId] = useState(null);

  useEffect(() => {
    if (accessToken) {
      fetchMenuItems(accessToken);
      fetchSystemPages(accessToken);
      fetchVisibilityOptions(accessToken);
    }
  }, [accessToken]);

  const handleUpdate = async (menuItemId, updateData) => {
    await updateMenuItem(menuItemId, updateData, accessToken);
  };

  const handleDelete = async (menuItemId) => {
    const confirmed = await confirm(
      'Are you sure you want to delete this menu item?',
      'Delete Menu Item',
      'Delete',
      'Cancel'
    );

    if (confirmed) {
      await deleteMenuItem(menuItemId, accessToken);
    }
  };

  const handleReorder = async (menuTypeCode, orderedIds) => {
    await reorderMenuItems(menuTypeCode, orderedIds, accessToken);
  };

  const handleAdd = (menuId) => {
    setCurrentMenuId(menuId);
    setAddModalOpen(true);
  };

  const handleAddSubmit = async (title) => {
    if (currentMenuId) {
      await createMenuItem(currentMenuId, { title }, accessToken);
    }
  };

  if (loading && menus.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-gray-600 dark:text-gray-400">Loading navigation...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">{error}</p>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Navigation Management</h1>

        {menus.map((menu) => (
          <MenuSection
            key={menu.menu_type_code}
            menu={menu}
            systemPages={systemPages}
            visibilityOptions={visibilityOptions}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onReorder={handleReorder}
            onAdd={handleAdd}
          />
        ))}
      </div>

      <AddMenuItemModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSubmit={handleAddSubmit}
      />
    </>
  );
}
